import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
// import '../styles/homepage-override.css';

const AITechPage: React.FC = () => {
  useEffect(() => {
    // Smooth scrolling for anchor links
    const anchors = document.querySelectorAll('a[href^="#"]');
    anchors.forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const href = (e.target as HTMLAnchorElement).getAttribute('href');
        if (href) {
          const target = document.querySelector(href);
          if (target) {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      });
    });

    // Add scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          (entry.target as HTMLElement).style.opacity = '1';
          (entry.target as HTMLElement).style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);
    
    // Observe all cards and sections for animations
    const elements = document.querySelectorAll('.offering-card, .pathway-card, .transformation-card, .interactive-tool, .tool-category');
    elements.forEach(el => {
      const element = el as HTMLElement;
      element.style.opacity = '0';
      element.style.transform = 'translateY(20px)';
      element.style.transition = 'all 0.6s ease';
      observer.observe(element);
    });

    // Stats counter animation
    const statsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const counters = entry.target.querySelectorAll('.stat-number');
          counters.forEach(counter => {
            const text = (counter as HTMLElement).textContent || '';
            let target;
            
            if (text.includes('%')) {
              target = parseInt(text);
            } else if (text.includes('K')) {
              target = parseInt(text.replace('£', '').replace('K', ''));
            } else if (text.includes('hrs')) {
              target = parseInt(text);
            } else if (text.includes('x')) {
              target = parseFloat(text);
            } else {
              target = parseInt(text);
            }
            
            animateCounter(counter as HTMLElement, target);
          });
          statsObserver.unobserve(entry.target);
        }
      });
    });
    
    const statsSection = document.querySelector('.success-stats');
    if (statsSection) {
      statsObserver.observe(statsSection.parentElement as Element);
    }

    // Inject ConvertKit script for AI Tech page form
    const script = document.createElement('script');
    script.src = 'https://fullness-life.kit.com/e923a152bb/index.js';
    script.async = true;
    script.setAttribute('data-uid', 'e923a152bb');
    document.getElementById('ck_embed_form_ai')?.appendChild(script);
    return () => {
      observer.disconnect();
      statsObserver.disconnect();
      document.getElementById('ck_embed_form_ai')?.removeChild(script);
    };
  }, []);

  const animateCounter = (element: HTMLElement, target: number, duration = 2000) => {
    let start = 0;
    const suffix = element.textContent?.replace(/[0-9.]/g, '') || '';
    const increment = target / (duration / 16);
    
    function updateCounter() {
      start += increment;
      if (start < target) {
        if (suffix === '%') {
          element.textContent = Math.floor(start) + '%';
        } else if (suffix.includes('K')) {
          element.textContent = '£' + Math.floor(start) + 'K';
        } else if (suffix === 'hrs') {
          element.textContent = Math.floor(start) + 'hrs';
        } else if (suffix === 'x') {
          element.textContent = (start).toFixed(1) + 'x';
        } else {
          element.textContent = Math.floor(start) + suffix;
        }
        requestAnimationFrame(updateCounter);
      } else {
        if (suffix === '%') {
          element.textContent = target + '%';
        } else if (suffix.includes('K')) {
          element.textContent = '£' + target + 'K';
        } else if (suffix === 'hrs') {
          element.textContent = target + 'hrs';
        } else if (suffix === 'x') {
          element.textContent = target + 'x';
        } else {
          element.textContent = target + suffix;
        }
      }
    }
    updateCounter();
  };

  return (
    <div className="bg-black text-white min-h-screen" style={{ 
      fontFamily: "'-apple-system', 'BlinkMacSystemFont', 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif"
    }}>
      <Navbar />
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes logoFloat {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-8px) rotate(1deg);
            }
          }
          
          @keyframes wireframeShift {
            0% {
              background-position: 0 0, 0 0, 0 0, 0 0;
            }
            100% {
              background-position: 12px 12px, 12px 12px, 8px 8px, -8px 8px;
            }
          }
          
          .wireframe-element {
            animation: wireframePulse 3s ease-in-out infinite;
          }
          
          .mesh-lines {
            animation: meshGlow 4s ease-in-out infinite;
          }
          
          @keyframes wireframePulse {
            0%, 100% {
              opacity: 0.9;
              stroke-width: 1.5;
            }
            50% {
              opacity: 1;
              stroke-width: 2;
            }
          }
          
          @keyframes meshGlow {
            0%, 100% {
              opacity: 0.3;
            }
            50% {
              opacity: 0.6;
            }
          }
          
          @keyframes pulseCorner {
            0%, 100% {
              opacity: 0.3;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.1);
            }
          }
          
          @keyframes textGlow {
            0%, 100% {
              text-shadow: 
                0 0 2px #f5f5dc,
                0 0 4px rgba(245, 245, 220, 0.8),
                0 0 6px rgba(245, 245, 220, 0.6);
            }
            50% {
              text-shadow: 
                0 0 4px #f5f5dc,
                0 0 8px rgba(245, 245, 220, 0.9),
                0 0 12px rgba(245, 245, 220, 0.7);
            }
          }
          
          .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 2rem;
          }
          
          .hero-section {
            min-height: 80vh;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            position: relative;
            overflow: hidden;
          }
          
          .hero-content h1 {
            font-size: clamp(2.5rem, 6vw, 4rem);
            font-weight: 500;
            letter-spacing: 0.02em;
            margin-bottom: 1rem;
            line-height: 1.1;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Display', 'Helvetica Neue', sans-serif;
            color: #f5f5dc;
            text-shadow: 
              0 0 1px #f5f5dc,
              0 0 3px rgba(245, 245, 220, 0.6),
              0 0 5px rgba(245, 245, 220, 0.4);
            position: relative;
            z-index: 2;
            filter: drop-shadow(0 0 6px rgba(245, 245, 220, 0.2));
          }
          
          .hero-subtitle {
            font-size: clamp(1.2rem, 3vw, 1.8rem);
            margin-bottom: 2rem;
            opacity: 0.9;
            font-weight: 400;
            color: rgba(245, 245, 220, 0.8);
            text-transform: uppercase;
            letter-spacing: 0.05em;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Text', 'Helvetica Neue', sans-serif;
          }
          
          .hero-story {
            font-size: 1.1rem;
            max-width: 600px;
            margin: 0 auto 3rem;
            opacity: 0.8;
            line-height: 1.7;
            color: #f5f5dc;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Text', 'Helvetica Neue', sans-serif;
          }
          
          .cta-button {
            background: #f5f5dc;
            color: #000;
            border: none;
            padding: 1.25rem 3rem;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            letter-spacing: 0.02em;
            transition: all 0.3s ease;
            box-shadow: 0 4px 16px rgba(245, 245, 220, 0.2);
            text-decoration: none;
            display: inline-block;
            border-radius: 4px;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Display', 'Helvetica Neue', sans-serif;
            position: relative;
            overflow: hidden;
          }
          
          .cta-button::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
            transition: left 0.5s;
          }
          
          .cta-button:hover::before {
            left: 100%;
          }
          
          .cta-button:hover {
            background: #e6e6cd;
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(245, 245, 220, 0.3);
          }
          
          .section {
            padding: 6rem 0;
          }
          
          .section h2 {
            font-size: clamp(2rem, 4vw, 3rem);
            font-weight: 500;
            text-align: center;
            margin-bottom: 3rem;
            color: #f5f5dc;
            text-shadow: 
              0 0 1px #f5f5dc,
              0 0 3px rgba(245, 245, 220, 0.6),
              0 0 5px rgba(245, 245, 220, 0.4);
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Display', 'Helvetica Neue', sans-serif;
            filter: drop-shadow(0 0 4px rgba(245, 245, 220, 0.2));
          }
          
          .ai-reality {
            background: #111;
            border: 1px solid #f5f5dc;
            padding: 3rem;
            margin: 2rem 0;
            text-align: center;
            border-radius: 4px;
          }
          
          .pathways-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2rem;
            margin: 3rem 0;
          }
          
          .pathway-card {
            background: #1a1a1a;
            border: 1px solid #333;
            padding: 2rem;
            text-align: center;
            transition: all 0.3s ease;
            border-radius: 4px;
            position: relative;
            overflow: hidden;
          }
          
          .pathway-card:hover {
            border-color: #f5f5dc;
            transform: translateY(-5px);
            box-shadow: 0 10px 30px rgba(245, 245, 220, 0.2);
          }
          
          .pathway-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 2px;
            background: linear-gradient(90deg, transparent, #f5f5dc, transparent);
            animation: shimmer 3s infinite;
          }
          
          @keyframes shimmer {
            0% { left: -100%; }
            100% { left: 100%; }
          }
          
          .pathway-card h3 {
            color: #f5f5dc;
            font-size: 1.5rem;
            margin-bottom: 1rem;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Display', 'Helvetica Neue', sans-serif;
          }
          
          .pathway-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
            display: block;
          }
          
          .offerings-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 2rem;
            margin: 3rem 0;
          }
          
          .offering-card {
            background: #1a1a1a;
            border: 1px solid #333;
            padding: 2rem;
            text-align: center;
            position: relative;
            overflow: hidden;
            border-radius: 4px;
            transition: all 0.3s ease;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }
          
          .offering-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 30px rgba(245, 245, 220, 0.2);
            border-color: #f5f5dc;
          }
          
          .offering-card.free {
            border-color: #00FF00;
          }
          
          .offering-card.low-ticket {
            border-color: #00BFFF;
          }
          
          .offering-card.mid-ticket {
            border-color: #f5f5dc;
          }
          
          .price-tag {
            position: absolute;
            top: 1rem;
            right: 1rem;
            background: #f5f5dc;
            color: #000;
            padding: 0.5rem 1rem;
            font-weight: 600;
            font-size: 0.9rem;
            border-radius: 20px;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Display', 'Helvetica Neue', sans-serif;
          }
          
          .free-tag {
            background: #00FF00;
            color: #000;
          }
          
          .low-ticket-tag {
            background: #00BFFF;
            color: #000;
          }
          
          .offering-card h3 {
            font-size: 1.4rem;
            margin-bottom: 1rem;
            color: #f5f5dc;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Display', 'Helvetica Neue', sans-serif;
          }
          
          .offering-card p {
            margin-bottom: 2rem;
            opacity: 0.8;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Text', 'Helvetica Neue', sans-serif;
            color: #f5f5dc;
          }
          
          .ai-stack-showcase {
            background: #0a0a0a;
            border-top: 2px solid #f5f5dc;
          }
          
          .tool-stack {
            background: #111;
            padding: 3rem;
            border-radius: 4px;
            margin: 2rem 0;
            border: 1px solid #f5f5dc;
          }
          
          .tool-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 2rem;
            margin: 2rem 0;
          }
          
          .tool-category {
            background: #1a1a1a;
            padding: 1.5rem;
            border-left: 3px solid #f5f5dc;
            border-radius: 4px;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }
          
          .tool-category h4 {
            color: #f5f5dc;
            margin-bottom: 0.5rem;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Display', 'Helvetica Neue', sans-serif;
          }
          
          .tool-list {
            list-style: none;
            margin-top: 1rem;
          }
          
          .tool-list li {
            margin: 0.5rem 0;
            opacity: 0.8;
            color: #f5f5dc;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Text', 'Helvetica Neue', sans-serif;
          }
          
          .success-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 2rem;
            margin: 3rem 0;
            text-align: center;
          }
          
          .stat-card {
            background: #1a1a1a;
            padding: 2rem;
            border-radius: 4px;
            border: 1px solid #333;
            transition: all 0.3s ease;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }
          
          .stat-card:hover {
            border-color: #f5f5dc;
            transform: translateY(-5px);
            box-shadow: 0 10px 30px rgba(245, 245, 220, 0.2);
          }
          
          .stat-number {
            font-size: 3rem;
            font-weight: 700;
            color: #f5f5dc;
            display: block;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Display', 'Helvetica Neue', sans-serif;
          }
          
          .stat-label {
            opacity: 0.8;
            margin-top: 0.5rem;
            color: #f5f5dc;
            font-family: '-apple-system', 'BlinkMacSystemFont', 'SF Pro Text', 'Helvetica Neue', sans-serif;
          }
          
          .career-transformation {
            background: #111;
            padding: 4rem 0;
          }
          
          .transformation-card {
            background: #1a1a1a;
            border: 1px solid #333;
            padding: 2rem;
            margin: 1rem;
            text-align: center;
            border-radius: 4px;
            position: relative;
          }
          
          .before-after {
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            gap: 2rem;
            align-items: center;
            margin: 2rem 0;
          }
          
          .before, .after {
            padding: 1.5rem;
            border-radius: 4px;
          }
          
          .before {
            background: rgba(255, 0, 0, 0.1);
            border: 1px solid rgba(255, 0, 0, 0.3);
          }
          
          .after {
            background: rgba(0, 255, 0, 0.1);
            border: 1px solid rgba(0, 255, 0, 0.3);
          }
          
          .arrow {
            font-size: 2rem;
            color: #f5f5dc;
          }
          
          .ai-tools-section {
            background: #0a0a0a;
          }
          
          .interactive-tool {
            background: #1a1a1a;
            border: 1px solid #333;
            padding: 2rem;
            margin: 1rem 0;
            cursor: pointer;
            transition: all 0.3s ease;
            border-radius: 4px;
          }
          
          .interactive-tool:hover {
            border-color: #f5f5dc;
            transform: translateX(10px);
          }
          
          .premium-preview {
            background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
            border: 2px solid #f5f5dc;
            padding: 3rem;
            text-align: center;
            margin: 3rem 0;
            border-radius: 4px;
          }
          
          .wireframe-bg {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            opacity: 0.05;
            background-image: 
                linear-gradient(rgba(245, 245, 220, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(245, 245, 220, 0.1) 1px, transparent 1px);
            background-size: 20px 20px;
            pointer-events: none;
          }

          /* Logo section styling */
          .logo-section {
            display: flex;
            justify-content: center;
            margin-bottom: 3rem;
            position: relative;
          }
          
          .logo-container {
            width: clamp(80px, 12vw, 120px);
            height: clamp(80px, 12vw, 120px);
            position: relative;
            animation: logoFloat 4s ease-in-out infinite;
          }
          
          .logo-container img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            filter: drop-shadow(0 0 10px rgba(245, 245, 220, 0.3));
          }
          
          @media (max-width: 768px) {
            .hero-section {
              min-height: 60vh;
            }
            
            .section {
              padding: 4rem 0;
            }
            
            .container {
              padding: 0 1rem;
            }
            
            .before-after {
              grid-template-columns: 1fr;
              gap: 1rem;
            }
            
            .arrow {
              transform: rotate(90deg);
            }
          }

          /* ConvertKit Form Styles */
          .ck-form * {
            box-sizing: border-box;
          }
          
          .ck-form {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            max-width: 500px;
            margin: 0 auto;
          }
          
          .ck-form .ck-fields {
            display: flex;
            flex-wrap: wrap;
            gap: 15px;
            margin-bottom: 20px;
          }
          
          .ck-form .ck-field {
            flex: 1;
            min-width: 200px;
          }
          
          .ck-form .ck-input {
            width: 100%;
            background: rgba(255, 255, 255, 0.95);
            font-size: 15px;
            padding: 12px 16px;
            border: 1px solid rgba(245, 245, 220, 0.3);
            border-radius: 4px;
            font-weight: 700;
            color: #000;
            transition: all 0.3s ease;
            font-family: inherit;
          }
          
          .ck-form .ck-input:focus {
            outline: none;
            border-color: #f5f5dc;
            box-shadow: 0 0 0 2px rgba(245, 245, 220, 0.2);
            background: #ffffff;
          }
          
          .ck-form .ck-input::placeholder {
            color: rgba(0, 0, 0, 0.6);
            opacity: 1;
          }
          
          .ck-form .ck-submit {
            background-color: #f5f5dc;
            color: #000;
            border: none;
            border-radius: 4px;
            font-weight: 600;
            font-size: 15px;
            padding: 12px 24px;
            cursor: pointer;
            transition: all 0.3s ease;
            width: 100%;
            font-family: inherit;
            letter-spacing: 0.02em;
          }
          
          .ck-form .ck-submit:hover {
            background-color: #e6e6cd;
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(245, 245, 220, 0.3);
          }
          
          .ck-form .ck-powered-by {
            text-align: center;
            margin-top: 15px;
          }
          
          .ck-form .ck-powered-by a {
            color: rgba(245, 245, 220, 0.6);
            text-decoration: none;
            font-size: 12px;
            transition: color 0.3s ease;
          }
          
          .ck-form .ck-powered-by a:hover {
            color: rgba(245, 245, 220, 0.8);
          }

          @media (max-width: 640px) {
            .ck-form .ck-fields {
              flex-direction: column;
            }
            
            .ck-form .ck-field {
              min-width: auto;
            }
          }
        `
      }} />
      
      {/* Hero Section */}
      <section className="hero-section">
        {/* Wireframe grid background - matching homepage */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          backgroundImage: `
            linear-gradient(rgba(245, 245, 220, 0.15) 0.5px, transparent 0.5px),
            linear-gradient(90deg, rgba(245, 245, 220, 0.15) 0.5px, transparent 0.5px),
            linear-gradient(45deg, rgba(245, 245, 220, 0.08) 0.5px, transparent 0.5px),
            linear-gradient(-45deg, rgba(245, 245, 220, 0.08) 0.5px, transparent 0.5px)
          `,
          backgroundSize: '12px 12px, 12px 12px, 8px 8px, 8px 8px',
          animation: 'wireframeShift 8s linear infinite',
          opacity: 0.6
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="hero-content">
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <h1>Enhance your Technical Skills</h1>
              
              {/* Wireframe grid overlay for title - matching homepage */}
              <div style={{
                position: 'absolute',
                top: '-10px',
                left: '-10px',
                right: '-10px',
                bottom: '-10px',
                zIndex: 1,
                backgroundImage: `
                  linear-gradient(rgba(245, 245, 220, 0.15) 0.5px, transparent 0.5px),
                  linear-gradient(90deg, rgba(245, 245, 220, 0.15) 0.5px, transparent 0.5px),
                  linear-gradient(45deg, rgba(245, 245, 220, 0.08) 0.5px, transparent 0.5px),
                  linear-gradient(-45deg, rgba(245, 245, 220, 0.08) 0.5px, transparent 0.5px)
                `,
                backgroundSize: '12px 12px, 12px 12px, 8px 8px, 8px 8px',
                animation: 'wireframeShift 8s linear infinite',
                opacity: 0.6
              }}></div>
              
              {/* Corner accent lines - matching homepage */}
              <div style={{
                position: 'absolute',
                top: '10%',
                left: '5%',
                width: '15px',
                height: '15px',
                border: '1px solid rgba(245, 245, 220, 0.4)',
                borderRight: 'none',
                borderBottom: 'none',
                animation: 'pulseCorner 3s ease-in-out infinite'
              }}></div>
              <div style={{
                position: 'absolute',
                top: '10%',
                right: '5%',
                width: '15px',
                height: '15px',
                border: '1px solid rgba(245, 245, 220, 0.4)',
                borderLeft: 'none',
                borderBottom: 'none',
                animation: 'pulseCorner 3s ease-in-out infinite 0.5s'
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '10%',
                left: '5%',
                width: '15px',
                height: '15px',
                border: '1px solid rgba(245, 245, 220, 0.4)',
                borderRight: 'none',
                borderTop: 'none',
                animation: 'pulseCorner 3s ease-in-out infinite 1s'
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '10%',
                right: '5%',
                width: '15px',
                height: '15px',
                border: '1px solid rgba(245, 245, 220, 0.4)',
                borderLeft: 'none',
                borderTop: 'none',
                animation: 'pulseCorner 3s ease-in-out infinite 1.5s'
              }}></div>
            </div>
            
            <p className="hero-subtitle">Master AI and improve your productivity</p>
            <div className="hero-story">
              <p>I've used AI to 10x my productivity, land better opportunities, and build multiple income streams. While others fear AI will replace them, I'm using it to become enhanced in my abilities.</p>
              <p><strong>The future belongs to those who use AI as their superpower, not those replaced by it.</strong></p> 
            </div>
          </div>
          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '4rem'
          }} id="ck_embed_form_ai"></div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="section">
        <div className="container">
          <h2>You've Spent Thousands on AI and Still Have Nothing to Show For It</h2>
          <p style={{ textAlign: 'center', color: 'rgba(245, 245, 220, 0.5)', marginBottom: '3rem', fontSize: '0.95rem' }}>sound familiar?</p>
          
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            {[
              "You've spent thousands on AI credits, subscriptions and premium plans — and still can't point to a single thing that's actually working in your business",
              "Every week you burn hours wrestling with prompts, watching tutorials, and testing new AI setups that never quite deliver what you imagined",
              "You had the idea months ago but you're still not at market — stuck in an endless loop of tweaking, rebuilding, and starting over with a different approach",
              "The gap between what AI promises and what you're actually getting feels embarrassing — you know it should be easier but you can't crack it alone",
              "You've invested serious time and money into figuring this out yourself, and the result is a graveyard of half-built projects, expired trials, and wasted weekends"
            ].map((point, i) => (
              <div key={i} style={{
                borderLeft: '2px solid rgba(245, 245, 220, 0.3)',
                paddingLeft: '1.5rem',
                marginBottom: '1.5rem',
                transition: 'all 0.3s ease'
              }}>
                <p style={{ color: 'rgba(245, 245, 220, 0.8)', fontSize: '1.05rem', lineHeight: '1.6' }}>
                  {point}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works — Discovery → Checkout Sequence */}
      <section className="section" style={{ borderTop: '1px solid rgba(245, 245, 220, 0.08)' }}>
        <div className="container">
          <h2>How It Works</h2>
          <div style={{ maxWidth: '700px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
            {[
              { num: "01", title: "Book a free discovery call", desc: "15 minutes. No pitch, no pressure. We'll dig into what you're trying to build, where AI is costing you time and money instead of saving it, and whether this mentorship is actually the right fit. If it's not, I'll tell you." },
              { num: "02", title: "Get your personalised AI blueprint", desc: "Before we even start, I'll map out a clear plan — exactly what we'll build, which workflows to install first, and the fastest path from where you are now to a working, AI-powered system. You'll know the game plan before you spend a penny." },
              { num: "03", title: "Enrol and we build it together", desc: "Pay once (£1,497) or book a 2-payment plan on the call. Then we get to work — 4 weekly 1-on-1 sessions where we implement AI into your actual business and life. Not theory. We build real systems, live, together. Plus 30 days of async support between sessions so you're never stuck." },
              { num: "04", title: "Launch, operate, and stay ahead", desc: "By day 30, you'll have AI embedded into your daily workflow, your content, your decisions — and the knowledge to keep evolving it yourself. No more wasted credits. No more half-built projects. You'll be the person others ask for AI advice." }
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
                <div style={{
                  width: '48px', height: '48px',
                  border: '2px solid #f5f5dc',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '1.2rem', color: '#f5f5dc', flexShrink: 0
                }}>{step.num}</div>
                <div>
                  <h3 style={{ color: '#f5f5dc', fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.5rem' }}>{step.title}</h3>
                  <p style={{ color: 'rgba(245, 245, 220, 0.65)', fontSize: '0.95rem', lineHeight: '1.6' }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Pathways */}
      {/* <section className="section ai-stack-showcase">
        <div className="container">
          <h2>4 AI-Powered Career Pathways</h2>
          <p style={{ textAlign: 'center', marginBottom: '3rem', fontSize: '1.2rem' }}>Choose your path to AI mastery and career acceleration</p>
          
          <div className="pathways-grid">
            <div className="pathway-card">
              <span className="pathway-icon">🚀</span>
              <h3>AI Career Accelerator</h3>
              <p>Use AI to land your dream job faster. Optimize resumes, ace interviews, and stand out from the competition.</p>
              <ul style={{ textAlign: 'left', marginTop: '1rem' }}>
                <li>AI-powered resume optimization</li>
                <li>Interview preparation with AI</li>
                <li>LinkedIn growth strategies</li>
                <li>Salary negotiation scripts</li>
              </ul>
            </div>
            
            <div className="pathway-card">
              <span className="pathway-icon">💼</span>
              <h3>Workplace AI Mastery</h3>
              <p>Become the AI expert at your company. Lead digital transformation and position yourself as indispensable.</p>
              <ul style={{ textAlign: 'left', marginTop: '1rem' }}>
                <li>Productivity tool mastery</li>
                <li>Process automation</li>
                <li>Team AI training</li>
                <li>Leadership positioning</li>
              </ul>
            </div>
            
            <div className="pathway-card">
              <span className="pathway-icon">🎯</span>
              <h3>AI Skill Building</h3>
              <p>Master the AI tools that matter. From content creation to data analysis, become proficient fast.</p>
              <ul style={{ textAlign: 'left', marginTop: '1rem' }}>
                <li>ChatGPT mastery course</li>
                <li>Creative AI tools</li>
                <li>Business automation</li>
                <li>Technical AI skills</li>
              </ul>
            </div>
            
            <div className="pathway-card">
              <span className="pathway-icon">🏢</span>
              <h3>AI Business Builder</h3>
              <p>Use AI to build and scale your business. From marketing to operations, AI-power your entrepreneurship.</p>
              <ul style={{ textAlign: 'left', marginTop: '1rem' }}>
                <li>AI marketing strategies</li>
                <li>Content automation</li>
                <li>Customer service AI</li>
                <li>Revenue optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* My AI Stack */}
      {/* <section className="section">
        <div className="container">
          <h2>My Complete AI Stack</h2>
          <p style={{ textAlign: 'center', marginBottom: '3rem' }}>The exact AI tools I use daily to build wealth in Abu Dhabi</p>
          
          <div className="tool-stack">
            <h3 style={{ textAlign: 'center', marginBottom: '2rem', color: '#f5f5dc' }}>$50K+ Value Created Daily With These Tools</h3>
            
            <div className="tool-grid">
              <div className="tool-category">
                <h4>🧠 Content Creation</h4>
                <ul className="tool-list">
                  <li><strong>ChatGPT-4:</strong> Scripts, captions, emails</li>
                  <li><strong>Claude:</strong> Long-form content</li>
                  <li><strong>Midjourney:</strong> Visual content</li>
                  <li><strong>Runway:</strong> Video editing</li>
                </ul>
              </div>
              
              <div className="tool-category">
                <h4>📊 Business Operations</h4>
                <ul className="tool-list">
                  <li><strong>Notion AI:</strong> Planning & organization</li>
                  <li><strong>Zapier:</strong> Workflow automation</li>
                  <li><strong>Calendly AI:</strong> Smart scheduling</li>
                  <li><strong>Grammarly:</strong> Communication optimization</li>
                </ul>
              </div>
              
              <div className="tool-category">
                <h4>🎯 Marketing & Sales</h4>
                <ul className="tool-list">
                  <li><strong>Copy.ai:</strong> Marketing copy</li>
                  <li><strong>Canva AI:</strong> Design automation</li>
                  <li><strong>HubSpot AI:</strong> CRM optimization</li>
                  <li><strong>Jasper:</strong> Brand voice consistency</li>
                </ul>
              </div>
              
              <div className="tool-category">
                <h4>⚡ Productivity</h4>
                <ul className="tool-list">
                  <li><strong>Otter.ai:</strong> Meeting transcription</li>
                  <li><strong>Loom AI:</strong> Video summaries</li>
                  <li><strong>Superhuman:</strong> Email AI</li>
                  <li><strong>Motion:</strong> AI task management</li>
                </ul>
              </div>
            </div>
            
            <p style={{ textAlign: 'center', marginTop: '2rem', fontStyle: 'italic', color: '#f5f5dc' }}>
              "These tools don't replace creativity - they amplify it. I spend less time on mundane tasks and more time on strategy and growth."
            </p>
          </div>
        </div>
      </section> */}

      {/* Offerings */}
      <section className="section" id="transform">
        <div className="container">
          <h2>Master AI and Technology for Success</h2>
          
          <h3 style={{ textAlign: 'center', margin: '3rem 0 2rem', color: '#4CAF50' }}>FREE AI Starters</h3>
          <div className="offerings-grid">
            {[
              {
                title: "AI in 2025",
                description: "Navigate the AI revolution with confidence",
                tag: "FREE",
                color: "#4CAF50"
              },
              {
                title: "AI is Only as Smart as Your Prompts",
                description: "Why most people aren't getting the better AI results",
                tag: "FREE",
                color: "#4CAF50"
              },
              {
                title: "AI Prompting: Lesson 1",
                description: "10x prompts results your today",
                tag: "FREE",
                color: "#4CAF50"
              }
            ].map((item, index) => (
              <div className="offering-card free">
                <div className="price-tag free-tag">{item.tag}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <a 
                  href="#ck_embed_form_ai" 
                  onClick={e => {
                    e.preventDefault();
                    const form = document.getElementById('ck_embed_form_ai');
                    let target: HTMLElement | null = form;
                    if (form) {
                      // Try to find the first input (first name field)
                      const input = form.querySelector('input[type="text"]');
                      if (input) target = input as HTMLElement;
                      if (target) {
                        const y = target.getBoundingClientRect().top + window.pageYOffset - 40; // 40px offset for nav
                        window.scrollTo({ top: y, behavior: 'smooth' });
                      }
                    }
                  }}
                  style={{
                    background: item.color,
                    color: '#000',
                    border: 'none',
                    padding: '1.25rem 3rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    letterSpacing: '0.02em',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 16px rgba(245, 245, 220, 0.2)',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}
                >
                  SIGN UP TO RECEIVE
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

          {/* <h3 style={{ textAlign: 'center', margin: '3rem 0 2rem', color: '#f5f5dc' }}>Complete AI Transformation (£197-£497)</h3>
          <div className="offerings-grid">
            <div className="offering-card mid-ticket">
              <div className="price-tag">£297</div>
              <h3>AI Career Transformation Program</h3>
              <p>8-week intensive covering all 4 pathways. Includes weekly coaching, AI tool access, and career acceleration guarantee.</p>
              <a href="#" className="cta-button">TRANSFORM MY CAREER</a>
            </div>
            
            <div className="offering-card mid-ticket">
              <div className="price-tag">£197</div>
              <h3>AI Business Builder Blueprint</h3>
              <p>Use AI to build and scale your business. From idea validation to growth automation, AI-power your entrepreneurship.</p>
              <a href="#" className="cta-button">BUILD WITH AI</a>
            </div>
            
            <div className="offering-card mid-ticket">
              <div className="price-tag">£497</div>
              <h3>AI Leadership Accelerator</h3>
              <p>Become the AI leader at your company. Includes team training materials, implementation roadmaps, and executive positioning.</p>
              <a href="#" className="cta-button">LEAD THE FUTURE</a>
            </div>
          </div> */}
        {/* </div>
      </section> */}

      {/* Success Stories */}
      {/* <section className="career-transformation">
        <div className="container">
          <h2>Real AI Career Transformations</h2>
          <p style={{ textAlign: 'center', marginBottom: '3rem' }}>Students who chose to lead instead of fear AI</p>
          
          <div className="success-stats">
            <div className="stat-card">
              <span className="stat-number">73%</span>
              <div className="stat-label">Got promotions within 6 months</div>
            </div>
            <div className="stat-card">
              <span className="stat-number">£18K</span>
              <div className="stat-label">Average salary increase</div>
            </div>
            <div className="stat-card">
              <span className="stat-number">15hrs</span>
              <div className="stat-label">Time saved per week</div>
            </div>
            <div className="stat-card">
              <span className="stat-number">2.4x</span>
              <div className="stat-label">Faster job search success</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '3rem' }}>
            <div className="transformation-card">
              <h4 style={{ color: '#f5f5dc', marginBottom: '1rem' }}>Sarah Chen - Marketing Manager</h4>
              <p>"Used AI to automate 80% of my content creation. Got promoted to Director with a £25K raise in 4 months."</p>
              <div style={{ marginTop: '1rem', color: '#00FF00', fontWeight: 600 }}>Result: £25K salary increase</div>
            </div>
            
            <div className="transformation-card">
              <h4 style={{ color: '#f5f5dc', marginBottom: '1rem' }}>James Wilson - Software Developer</h4>
              <p>"AI coding assistants made me 3x more productive. Now leading AI adoption at my company."</p>
              <div style={{ marginTop: '1rem', color: '#00FF00', fontWeight: 600 }}>Result: Team Lead promotion</div>
            </div>
            
            <div className="transformation-card">
              <h4 style={{ color: '#f5f5dc', marginBottom: '1rem' }}>Maria Rodriguez - Consultant</h4>
              <p>"Built an AI-powered consultancy that generated £150K revenue in first year using the tools I learned."</p>
              <div style={{ marginTop: '1rem', color: '#00FF00', fontWeight: 600 }}>Result: £150K business launch</div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Interactive AI Tools */}
      {/* <section className="ai-tools-section section">
        <div className="container">
          <h2>Free AI Career Tools</h2>
          <p style={{ textAlign: 'center', marginBottom: '3rem' }}>Instant AI-powered career optimization</p>
          
          <div className="interactive-tool" onClick={() => openAITool('resume')}>
            <h3>🤖 AI Resume Optimizer</h3>
            <p>Upload your resume and get AI-powered optimization suggestions for better ATS performance and recruiter appeal.</p>
            <div style={{ marginTop: '1rem', color: '#f5f5dc', fontWeight: 600 }}>Optimize Resume →</div>
          </div>
          
          <div className="interactive-tool" onClick={() => openAITool('salary')}>
            <h3>💰 AI Salary Negotiation Coach</h3>
            <p>Get personalized salary negotiation scripts and strategies based on your role, experience, and market data.</p>
            <div style={{ marginTop: '1rem', color: '#f5f5dc', fontWeight: 600 }}>Get Negotiation Strategy →</div>
          </div>
          
          <div className="interactive-tool" onClick={() => openAITool('skills')}>
            <h3>🎯 AI Skills Gap Analyzer</h3>
            <p>Analyze current skills vs future job requirements. Get your personalized learning roadmap for career growth.</p>
            <div style={{ marginTop: '1rem', color: '#f5f5dc', fontWeight: 600 }}>Analyze Skills →</div>
          </div>
          
          <div className="interactive-tool" onClick={() => openAITool('interview')}>
            <h3>🎤 AI Interview Simulator</h3>
            <p>Practice interviews with AI that adapts to your responses. Get real-time feedback on your answers.</p>
            <div style={{ marginTop: '1rem', color: '#f5f5dc', fontWeight: 600 }}>Start Practice →</div>
          </div>
        </div>
      </section> */}

      {/* Premium Community Preview */}
      {/* <section className="section">
        <div className="container">
          <div className="premium-preview">
            <h2>AI Mastery Elite Community</h2>
            <p style={{ fontSize: '1.3rem', margin: '2rem 0' }}>Join 1,200+ professionals mastering AI for career acceleration</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', margin: '3rem 0' }}>
              <div>
                <h4>Weekly AI Tool Updates</h4>
                <p>Stay ahead with the latest AI tools and techniques</p>
              </div>
              <div>
                <h4>Career Coaching Calls</h4>
                <p>Monthly group sessions for AI career strategy</p>
              </div>
              <div>
                <h4>Exclusive AI Resources</h4>
                <p>Advanced prompts, workflows, and automation scripts</p>
              </div>
              <div>
                <h4>Industry AI Reports</h4>
                <p>How AI is transforming your specific industry</p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Final CTA */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          {/* <h2>Ready to Future-Proof Your Career?</h2> */}
          <p style={{ fontSize: '1.3rem', margin: '2rem 0', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
            Stop fearing AI. Start mastering it. The professionals who learn AI today will lead tomorrow.
          </p>
          
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap', margin: '3rem 0' }}>
            <a href="#transform" className="cta-button">START WITH FREE AI TOOLS</a>
            {/* <a href="#" className="cta-button" style={{ background: 'transparent', border: '2px solid #f5f5dc', color: '#f5f5dc' }}>GET AI MASTERY PROGRAM</a> */}
          </div>
          
          {/* <p style={{ marginTop: '2rem', opacity: 0.8 }}>
            Join 12,000+ professionals building AI-powered careers with Fullness Life
          </p> */}
        </div>
      </section>
    </div>
  );
};

export default AITechPage; 